import * as planck from "planck-js";
import { Body, Fixture, Vec2, World } from "planck-js";
import { Component } from "../core/Component";
import { GameObject } from "../core/GameObject";
import { GameScene } from "../scenes/GameScene";

export type PlanckBodyData = {
    x: number;
    y: number;
    center?: Vec2;
    angle?: number;
    width?: number;
    height?: number;
    radius?: number;
    isStatic?: boolean;
    isDynamic?: boolean;
    isKinematic?: boolean;
    isSensor?: boolean;
    isSleepingAllowed?: boolean;
    friction?: number;
    restitution?: number;
    density?: number;
    massData?: MassData;
    filterCategoryBits?: number;
    filterMaskBits?: number;
    filterGroupIndex?: number;
    worldScale: number;
};

export type MassData = {
    mass?: number;
    center?: planck.Vec2;
    I?: number;
};

type FixtureOptions = {
    friction?: number;
    restitution?: number;
    density?: number;
    filterCategoryBits?: number;
    filterMaskBits?: number;
    filterGroupIndex?: number;
};

export enum PlanckBodyType {
    NONE = 0,
    BOX = 1,
    CIRCLE = 2,
}

export class PlanckCollisonEvent {
    static ON_BEGIN_CONTACT = "begin-contact";
    static ON_PRE_SOLVE = "pre-solve";
    static ON_POST_SOLVE = "post-solve";
}

export class PlanckBodyComponent extends Component {
    scene: GameScene;
    type: PlanckBodyType;
    data: PlanckBodyData;
    world: World;
    body: Body;
    fixture: Fixture;
    worldScale: number;
    mass = 1;

    get position(): Vec2 {
        return this.body ? this.body.getPosition() : Vec2();
    }

    get velocity(): Vec2 {
        return this.body ? this.body.getLinearVelocity() : Vec2();
    }

    constructor(obj: GameObject, type: PlanckBodyType, data: PlanckBodyData) {
        super(obj);
        this.world = this.scene.world;
        this.worldScale = data.worldScale;
        this.setBody(type, data);
    }

    destroy(): void {
        this.world.destroyBody(this.body);
        // can't destroy a body during a physics callback, so wait a bit to make sure it really gets destroyed
        this.scene.time.delayedCall(1, () => this.world.destroyBody(this.body));
        super.destroy();
    }

    setBody(type: PlanckBodyType, data: PlanckBodyData): Body {
        this.type = type;
        this.data = data;

        const body = (this.body = this.world.createBody());
        if (data.isStatic === true) body.setStatic();
        if (data.isDynamic === true) body.setDynamic();
        if (data.isKinematic === true) body.setKinematic();
        if (data.isSleepingAllowed != null) body.setSleepingAllowed(data.isSleepingAllowed);
        if (data.massData) this.setMassData(data.massData);

        this.addFixture(type, data);
        this.setPosition(data.x, data.y);

        if (data.isSensor === true) this.setSensor(true);

        if (data.angle) {
            body.setAngle(data.angle);
        }

        body.setUserData(this.obj);
        return body;
    }

    addFixture(type: PlanckBodyType, data: PlanckBodyData): Fixture | null {
        const fixtureOptions: FixtureOptions = {
            friction: data.friction || 1.0,
            restitution: data.restitution || 0.0,
            density: data.density || 1.0,
        };

        if (data.filterCategoryBits) fixtureOptions.filterCategoryBits = data.filterCategoryBits;
        if (data.filterMaskBits) fixtureOptions.filterMaskBits = data.filterMaskBits;
        if (data.filterGroupIndex) fixtureOptions.filterGroupIndex = data.filterGroupIndex;

        switch (type) {
            case PlanckBodyType.BOX:
                if (data.width === undefined || data.height === undefined) break;
                this.fixture = this.createBoxFixture(
                    data.width / 2 / data.worldScale,
                    data.height / 2 / data.worldScale,
                    data.center,
                    data.angle,
                    fixtureOptions
                );
                this.fixture.setUserData(this);
                return this.fixture;
            case PlanckBodyType.CIRCLE:
                if (data.radius === undefined) break;
                this.fixture = this.createCircleFixture(data.radius / data.worldScale, fixtureOptions);
                this.fixture.setUserData(this);
                return this.fixture;
            default:
                return null;
        }
        return null;
    }

    createBoxFixture(hx: number, hy: number, center?: Vec2, angle?: number, fixtureOptions?: FixtureOptions): Fixture {
        return this.body.createFixture(planck.Box(hx, hy, center, angle), fixtureOptions);
    }

    createCircleFixture(radius: number, fixtureOptions?: FixtureOptions): planck.Fixture {
        return this.body.createFixture(planck.Circle(radius), fixtureOptions);
    }

    update(dt: number): void {
        super.update(dt);
        const bodyPos = this.getPosition();
        this.obj.setPosition(bodyPos.x * this.worldScale, bodyPos.y * this.worldScale);
        this.obj.setRotation(this.getAngle());
    }

    setSensor(value: boolean): void {
        if (!this.fixture) return;
        this.fixture.setSensor(value);
    }

    isSensor(): boolean {
        if (!this.fixture) return false;
        return this.fixture.isSensor();
    }

    isStatic(): boolean {
        return this.body.isStatic();
    }

    isKinimatic(): boolean {
        return this.body.isKinematic();
    }

    setPosition(x?: number, y?: number): void {
        if (!x || !y) {
            this.body.setPosition(Vec2(this.obj.x / this.worldScale, this.obj.y / this.worldScale));
        } else {
            this.body.setPosition(Vec2(x / this.worldScale, y / this.worldScale));
        }
        
        const bodyPos = this.getPosition();
        this.obj.setPosition(bodyPos.x * this.worldScale, bodyPos.y * this.worldScale);
    }

    setAngle(angle: number): void {
        this.body.setAngle(angle);
    }

    setLinearVelocity(x: number, y: number): void {
        this.body.setLinearVelocity(Vec2(x, y));
    }

    setDynamic(): void {
        this.body.setDynamic();
    }

    setStatic(): void {
        this.body.setStatic();
    }

    setKinematic(): void {
        this.body.setKinematic();
    }

    setActive(active: boolean): void {
        this.body.setActive(active);
    }

    setMassData(newMassData?: MassData): void {
        const massData = {
            mass: this.mass,
            center: Vec2(),
            I: 1,
        };
        if (newMassData) {
            if (newMassData.mass) massData.mass = newMassData.mass;
            if (newMassData.center) massData.center = newMassData.center;
            if (newMassData.I) massData.I = newMassData.I;
        }
        this.body.setMassData(massData);
    }

    setGravityScale(scale: number): void {
        this.body.setGravityScale(scale);
    }

    setFixedRotation(fixedRotation: boolean): void {
        this.body.setFixedRotation(fixedRotation);
    }

    getPosition(): Vec2 {
        return this.body.getPosition();
    }

    getWorldCenter(): Vec2 {
        return this.body.getWorldCenter();
    }

    getWorldPoint(localPoint: Vec2): Vec2 {
        return this.body.getWorldPoint(localPoint);
    }

    getAngle(): number {
        return this.body.getAngle();
    }

    applyForce(force: Vec2, position: Vec2): void {
        this.body.applyForce(force, position);
    }

    applyForceToCenter(force: Vec2): void {
        this.body.applyForceToCenter(force);
    }

    applyLinearImpulse(impulse: Vec2, point: Vec2): void {
        this.body.applyLinearImpulse(impulse, point);
    }

    addCollisionListener(event: string, listener: (contact: planck.Contact) => any): void {
        switch (event) {
            case PlanckCollisonEvent.ON_BEGIN_CONTACT:
                this.world.on("begin-contact", listener);
                break;
            case PlanckCollisonEvent.ON_PRE_SOLVE:
                this.world.on("pre-solve", listener);
                break;
            case PlanckCollisonEvent.ON_POST_SOLVE:
                this.world.on("post-solve", listener);
                break;
        }
    }

    removeCollisionListener(event: string, listener: (contact: planck.Contact) => any): void {
        switch (event) {
            case PlanckCollisonEvent.ON_BEGIN_CONTACT:
                this.world.off("begin-contact", listener);
                break;
            case PlanckCollisonEvent.ON_PRE_SOLVE:
                this.world.off("pre-solve", listener);
                break;
        }
    }
}
